/**
 * @file Notification services for quote system
 * Provides functions for sending notifications via different channels
 */

import { withErrorHandling } from '@/lib/errors';
import { Quote } from '@/lib/types';
import { ContactMethod, QuoteNotificationParams } from '@/lib/types/quote-types';
import { generateQuoteToken, constructMagicLink } from "./tokens";
import { supabase } from "@/integrations/supabase/client";
import { debug } from "@/lib/utils";
import { config } from "@/lib/config";

// Storage key for notification queue
const NOTIFICATION_QUEUE_KEY = 'pendingQuoteNotifications';

// Notification queue type
export interface QueuedNotification {
  quote: Quote;
  contactMethod: ContactMethod;
  customMessage?: string;
  email?: string;
}

// Initialize queue from localStorage
const loadNotificationQueue = (): QueuedNotification[] => {
  try {
    const storedQueue = localStorage.getItem(NOTIFICATION_QUEUE_KEY);
    return storedQueue ? JSON.parse(storedQueue) : [];
  } catch (error) {
    debug.error('Error loading notification queue from localStorage:', error);
    return [];
  }
}

// Notification queue state
let notificationQueue: QueuedNotification[] = loadNotificationQueue();

/**
 * Notification result interface
 */
interface NotificationResult {
  /** Whether the notification was sent successfully */
  success: boolean;
  /** The method used to send the notification */
  method: ContactMethod;
  /** Any error that occurred */
  error?: Error;
}

/**
 * Send a notification to the tradesperson about a new quote request
 * Supports multiple contact methods with fallback options
 *
 * @param quote The quote object with tradesperson info
 * @param contactMethod The preferred contact method ('email' or 'phone')
 * @param customMessage Optional custom message to include
 * @returns Result of the notification attempt
 */
// Function overload declarations
export function sendQuoteRequestNotification(
  quote: Quote,
  contactMethod?: ContactMethod,
  customMessage?: string,
  email?: string
): Promise<NotificationResult>;

export function sendQuoteRequestNotification(
  params: QuoteNotificationParams
): Promise<NotificationResult>;

// Implementation that handles both overloads
/**
 * Add a notification to the queue
 * @param quote The quote to send notification for
 * @param contactMethod The preferred contact method
 * @param customMessage Optional custom message
 * @param email Optional user email to use for contact
 * @returns The updated queue length
 */
export const addToNotificationQueue = (
  quote: Quote,
  contactMethod: ContactMethod = 'email',
  customMessage?: string,
  email?: string
): number => {
  const queueItem: QueuedNotification = {
    quote,
    contactMethod,
    customMessage,
    email
  };

  notificationQueue.push(queueItem);

  // Update localStorage
  try {
    localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(notificationQueue));
  } catch (error) {
    debug.error('Error saving notification queue to localStorage:', error);
  }

  return notificationQueue.length;
};

/**
 * Get the current notification queue
 * @returns Array of queued notifications
 */
export const getNotificationQueue = (): QueuedNotification[] => {
  return [...notificationQueue]; // Return a copy
};

/**
 * Process the next notification in the queue
 * @returns Promise resolving to success or failure
 */
export const processNextNotification = async (): Promise<boolean> => {
  if (notificationQueue.length === 0) {
    return false;
  }

  const nextNotification = notificationQueue[0];

  try {
    // Send the notification
    const result = await sendQuoteRequestNotification(
      nextNotification.quote,
      nextNotification.contactMethod,
      nextNotification.customMessage,
      nextNotification.email
    );

    if (result.success) {
      // Remove from queue on success
      notificationQueue.shift();
      localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(notificationQueue));
      return true;
    } else {
      debug.error('Failed to send notification from queue:', result.error);
      return false;
    }
  } catch (error) {
    debug.error('Error processing notification from queue:', error);
    return false;
  }
};

/**
 * Clear the notification queue
 */
export const clearNotificationQueue = (): void => {
  notificationQueue = [];
  localStorage.removeItem(NOTIFICATION_QUEUE_KEY);
};

/**
 * Get the queue length
 * @returns Number of pending notifications
 */
export const getNotificationQueueLength = (): number => {
  return notificationQueue.length;
};

export const sendQuoteRequestNotification = withErrorHandling(
  async (
    quoteOrParams: Quote | QuoteNotificationParams,
    contactMethodArg?: ContactMethod,
    customMessageArg?: string,
    emailArg?: string
  ): Promise<NotificationResult> => {
    // Handle overloaded parameters
    let quote: Quote;
    let contactMethod: ContactMethod;
    let customMessage: string | undefined;
    let email: string | undefined;

    if ('quote' in quoteOrParams) {
      // It's the params object
      quote = quoteOrParams.quote;
      contactMethod = quoteOrParams.contactMethod;
      customMessage = quoteOrParams.customMessage;
      email = quoteOrParams.email;
    } else {
      // It's the quote object with separate parameters
      quote = quoteOrParams;
      contactMethod = contactMethodArg || 'email';
      customMessage = customMessageArg;
      email = emailArg;
    }

    const tradesperson = quote.tradesperson;

    if (!tradesperson) {
      throw new Error('No tradesperson associated with quote');
    }

    // Get the user's profile information
    const { data: userProfile, error: profileError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", quote.user_id)
      .single();

    if (profileError) {
      debug.error('Error loading user profile:', profileError);
      // Continue without the profile information rather than failing
    }

    // Use the test email in the MVP if enabled
    const useTestMode = config.features.useTestEmailForAll;

    // Generate token for the magic link
    const token = await generateQuoteToken(quote.id, quote.trade_id);

    // Build the magic link URL
    const responseUrl = constructMagicLink(token);

    // Get job details
    const jobTitle = quote.job?.title || 'New job request';
    const jobDescription = quote.job?.description || 'No description provided';

    // Create a shorter, more focused message suitable for both email and SMS
    const subject = `Need help with ${jobTitle}`;

    // Determine email signature - prefer provided email, then profile email
    const userSignature = email
      ? email.split('@')[0]
      : (userProfile?.email ? userProfile.email.split('@')[0] : 'A local customer');

    // Use custom message if provided, otherwise generate the standard one
    const body = customMessage || `
Hi ${tradesperson.contact},

I need help with ${jobTitle.toLowerCase()}${userProfile?.postcode ? ' at my ' + (userProfile?.property_type?.toLowerCase() || 'property') + ' in ' + userProfile.postcode : ''}.

${jobDescription.length > 150 ? jobDescription.substring(0, 150) + '...' : jobDescription}

👉 Respond here: ${responseUrl}

Thanks,
${userSignature}
    `.trim();

    // Mark notification as sent in the database
    await supabase
      .from("quotes")
      .update({
        notification_sent_at: new Date().toISOString()
      })
      .eq("id", quote.id);

    // Result to return
    const result: NotificationResult = {
      success: true,
      method: contactMethod
    };

    try {
      // Handle contact method
      if (contactMethod === 'email') {
        // Determine which email to use
        const recipientEmail = useTestMode
          ? config.testEmail
          : tradesperson.email || config.testEmail;

        // Create the mailto: URL
        const mailtoUrl = `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

        // Open the email client using window.open() which preserves our app context
        try {
          // Use _self for Safari Mobile which handles mailto: links better this way
          const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                                !(window as Window & { MSStream?: unknown }).MSStream &&
                                /Safari/.test(navigator.userAgent);

          // Success is best determined by whether the window opened, not later status
          const mailWindow = isMobileSafari
            ? window.open(mailtoUrl, '_self')
            : window.open(mailtoUrl, '_blank');

          if (!mailWindow && !isMobileSafari) { // _self returns null in Safari, so don't check there
            throw new Error('Email client window failed to open');
          }

          debug.log('Email client opened successfully');
        } catch (emailError) {
          debug.error('Error launching email client:', emailError);
          result.success = false;
          result.error = emailError instanceof Error
            ? emailError
            : new Error('Failed to launch email client');
        }
      } else if (contactMethod === 'phone' || contactMethod === 'sms') {
        // SMS/Phone contact method
        const recipientPhone = useTestMode
          ? config.testPhone
          : tradesperson.phone;

        if (!recipientPhone) {
          // Fallback to email if no phone available
          debug.log('No phone number available, falling back to email');
          const fallbackEmail = useTestMode ? config.testEmail : (tradesperson.email || config.testEmail);
          const mailtoUrl = `mailto:${fallbackEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          // Use the same approach as the primary email sending
          const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                               !(window as Window & { MSStream?: unknown }).MSStream &&
                               /Safari/.test(navigator.userAgent);

          if (isMobileSafari) {
            window.open(mailtoUrl, '_self');
          } else {
            window.open(mailtoUrl, '_blank');
          }

          // Update result with fallback method
          result.method = 'email';
        } else {
          // Create SMS URL - format depends on platform
          let smsUrl = '';

          // iOS uses different format than Android/web
          // MSStream is a property added by Microsoft in IE mobile to help with UA identification
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;

          if (isIOS) {
            // iOS uses &body
            smsUrl = `sms:${recipientPhone}&body=${encodeURIComponent(body)}`;
          } else {
            // Android and others use ?body
            smsUrl = `sms:${recipientPhone}?body=${encodeURIComponent(body)}`;
          }

          // Open SMS app using the same approach as email
          const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                              !(window as Window & { MSStream?: unknown }).MSStream &&
                              /Safari/.test(navigator.userAgent);

          if (isMobileSafari) {
            window.open(smsUrl, '_self');
          } else {
            window.open(smsUrl, '_blank');
          }
        }
      }
    } catch (error) {
      // Update result with error
      result.success = false;
      result.error = error instanceof Error
        ? error
        : new Error('Unknown error occurred during notification');

      // Log error but still return
      debug.error('Error sending notification:', error);
    }

    return result;
  },
  { success: false, method: 'email' }, // Return error result on failure
  'Failed to send quote notification'
);
